import { useState } from 'react';
import { DAY_OF_WEEK_LABELS, type CustomerReport } from '@catalyst-psa/shared';

import { useAuth } from '../../auth/authContext.js';
import { PageHeader } from '../../components/layout/PageHeader.js';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { DataState } from '../../components/ui/DataState.js';
import { CustomerReportFormModal } from './CustomerReportFormModal.js';
import { useCustomerReports, useSendCustomerReport } from './api.js';

function scheduleLabel(report: CustomerReport): string {
  return `${DAY_OF_WEEK_LABELS[report.dayOfWeek] ?? '—'} at ${String(report.hourOfDay).padStart(
    2,
    '0',
  )}:00`;
}

function lastRunBadge(report: CustomerReport): JSX.Element {
  if (report.lastRunStatus === 'ok') return <Badge tone="green">Last run OK</Badge>;
  if (report.lastRunStatus === 'failed') return <Badge tone="red">Last run failed</Badge>;
  return <Badge tone="gray">Never sent</Badge>;
}

export function CustomerReportsPage(): JSX.Element {
  const { isAdmin } = useAuth();
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<CustomerReport | null>(null);

  const query = useCustomerReports();
  const reports = query.data ?? [];
  const sendMutation = useSendCustomerReport();
  const [sendingId, setSendingId] = useState<string | null>(null);

  const openCreate = (): void => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (report: CustomerReport): void => {
    setEditing(report);
    setFormOpen(true);
  };

  const send = (report: CustomerReport): void => {
    setSendingId(report.id);
    sendMutation.mutate(report.id, { onSettled: () => setSendingId(null) });
  };

  return (
    <div>
      <PageHeader
        title="Customer Reports"
        description="Weekly customer-facing status reports — one filtered report per enrolled customer."
        actions={
          isAdmin ? (
            <Button variant="primary" onClick={openCreate}>
              New report
            </Button>
          ) : undefined
        }
      />

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Schedule</th>
              <th className="px-4 py-3">Customers</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            <DataState
              isLoading={query.isLoading}
              isError={query.isError}
              error={query.error}
              isEmpty={reports.length === 0}
              emptyTitle="No customer reports yet"
              emptyDescription="Create a report, add customers and choose their recipient contacts."
              onRetry={() => void query.refetch()}
              colSpan={5}
            >
              {reports.map((report) => (
                <tr
                  key={report.id}
                  className="cursor-pointer hover:bg-slate-50"
                  onClick={() => openEdit(report)}
                >
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-800">{report.name}</div>
                    {report.description ? (
                      <div className="text-xs text-slate-500">{report.description}</div>
                    ) : null}
                  </td>
                  <td className="px-4 py-3">
                    {report.enabled ? (
                      <Badge tone="blue">{scheduleLabel(report)}</Badge>
                    ) : (
                      <Badge tone="gray">Paused</Badge>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-700">{report.enrollments.length}</td>
                  <td className="px-4 py-3">{lastRunBadge(report)}</td>
                  <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                    {isAdmin ? (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => send(report)}
                        isLoading={sendMutation.isPending && sendingId === report.id}
                        disabled={sendMutation.isPending || report.enrollments.length === 0}
                      >
                        Send now
                      </Button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </DataState>
          </tbody>
        </table>
      </div>

      <CustomerReportFormModal
        isOpen={formOpen}
        report={editing}
        onClose={() => setFormOpen(false)}
      />
    </div>
  );
}
